// src/Pages/views/Invoices/InvoiceContainer.tsx
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { groupBy } from "lodash";
import { updateInvoiceField } from "src/redux/reducers/InvoiceManager";
import InvoiceQbo from "./InvoiceQbo";
import InvoiceExcel from "./InvoiceExcel";
import { ReduxState } from "src/redux";
import { Invoice2 } from "src/docuclipper/DocuclipperTypes";
import {
  Col,
  FormGroup,
  Row,
  Form,
  Button,
  Badge,
  Modal,
} from "react-bootstrap";
import ExportTypeInvoiceDropdown from "../DataArchive/TransactionManager/DownloadOptions/ExportTypeInvoiceDropdown";
import { DownloadInvoiceModal } from "../DataArchive/TransactionManager/DownloadInvoiceModal";
import { Formik } from "formik";
import { ApprovalHistory } from "./ApprovalHistory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InvoiceTransactionMatcher, {
  InvoiceMatchStatus,
} from "./InvoiceTransactionMatcher";
import InvoiceApprovalRules from "./InvoiceApprovalRules";
import { InvoicePOMatchManager } from "./InvoiceMatching/InvoicePOMatchManager";
import { InvoicePOMatchStatus } from "./InvoicePOMatchStatus";
import { createAlert } from "src/redux/reducers/Alerts";
import { setShowModalInvoiceRules } from "src/redux/reducers/InvoiceManager";
import {
  fetchApprovalStatus,
  approveInvoice,
  rejectInvoice,
  submitInvoiceForApproval,
  resetState,
} from "src/redux/reducers/InvoiceApprovalStatus";

const InvoiceContainer: React.FC = () => {
  const dispatch = useDispatch();
  const [showHistory, setShowHistory] = useState(false);
  const [showMatcher, setShowMatcher] = useState(false);
  const [showPOMatcher, setShowPOMatcher] = useState(false);
  const [showApprovalRules, setShowApprovalRules] = useState(false);
  const [matchStatusKey, setMatchStatusKey] = useState(0); // Force refresh of match status
  const [poMatchStatusKey, setPoMatchStatusKey] = useState(0); // Force refresh of PO match status
  const [approvalComment, setApprovalComment] = useState("");

  const { documentId, transactionLineRowNumber, transactionLines } =
    useSelector((state: ReduxState) => state.InvoiceManager);
  const exportTo = useSelector(
    (state: ReduxState) => state.DownloadInvoiceOptions.selectedExportType
  );
  const approvalStatus = useSelector(
    (state: ReduxState) => state.InvoiceApprovalStatus
  );

  const selectedDocumentId =
    documentId || transactionLines[0]?.documentId || "";

  const tlsByDocId = groupBy(transactionLines, "documentId");

  const selectedTransactionLine =
    transactionLineRowNumber !== null
      ? tlsByDocId?.[selectedDocumentId]?.[transactionLineRowNumber]
      : null;
  const invoice = selectedTransactionLine?.row
    ? (JSON.parse(selectedTransactionLine.row as any) as Invoice2)
    : null;

  // Fetch approval status
  useEffect(() => {
    if (selectedTransactionLine?.id) {
      dispatch(fetchApprovalStatus(selectedTransactionLine.id));
    } else {
      dispatch(resetState());
    }
  }, [selectedTransactionLine?.id, JSON.stringify(invoice), dispatch]);

  // Early returns after all hooks
  if (transactionLineRowNumber === null) return null;
  if (!invoice) return null;

  const handleFieldChange = (field: string, value: any) => {
    dispatch(updateInvoiceField({ invoice, field, value }));
  };

  const handleMatchesUpdated = () => {
    // Force refresh of match status by updating the key
    setMatchStatusKey((prev) => prev + 1);
  };

  const handlePOMatchesUpdated = () => {
    // Force refresh of PO match status by updating the key
    setPoMatchStatusKey((prev) => prev + 1);
  };

  // Approval handlers
  const handleApprove = async () => {
    if (!selectedTransactionLine?.id) return;
    try {
      await dispatch(
        approveInvoice(Number(selectedTransactionLine.id), approvalComment)
      );
      setApprovalComment("");
      dispatch(
        createAlert({
          type: "success",
          message: "Invoice approved successfully",
          timeout: 3000,
        })
      );
    } catch (error) {
      dispatch(
        createAlert({
          type: "error",
          message: "Error approving invoice",
          timeout: 5000,
        })
      );
    }
  };

  const handleReject = async () => {
    if (!selectedTransactionLine?.id) return;
    try {
      await dispatch(
        rejectInvoice(Number(selectedTransactionLine.id), approvalComment)
      );
      setApprovalComment("");
      dispatch(
        createAlert({
          type: "success",
          message: "Invoice rejected",
          timeout: 3000,
        })
      );
    } catch (error) {
      dispatch(
        createAlert({
          type: "error",
          message: "Error rejecting invoice",
          timeout: 5000,
        })
      );
    }
  };

  const handleSubmitForApproval = async () => {
    if (!selectedTransactionLine?.id) return;
    try {
      await dispatch(submitInvoiceForApproval(selectedTransactionLine.id));
      dispatch(
        createAlert({
          type: "success",
          message: "Invoice submitted for approval",
          timeout: 3000,
        })
      );
    } catch (error) {
      dispatch(
        createAlert({
          type: "error",
          message: "Error submitting for approval",
          timeout: 5000,
        })
      );
    }
  };

  const getMarginTop = (index, lineItem, margin, defaultMargin) => {
    if (index !== 0) {
      return defaultMargin;
    }
    if (
      ["bill", "expense"].includes(invoice.docType) &&
      (lineItem.categoryEdited || lineItem.categoryAutomated)
    ) {
      return margin;
    }
    if (
      ["invoice"].includes(invoice.docType) &&
      (lineItem.serviceEdited || lineItem.serviceAutomated)
    ) {
      return margin;
    }
    if (
      ["payables"].includes(invoice.docType) &&
      (lineItem.categoryExcelEdited || lineItem.categoryExcel)
    ) {
      return margin;
    }
    if (
      ["receivables"].includes(invoice.docType) &&
      (lineItem.serviceExcelEdited || lineItem.serviceExcel)
    ) {
      return margin;
    }
    return defaultMargin;
  };

  const documentTypeOptions =
    exportTo === "qbo"
      ? ["expense", "invoice", "bill"]
      : ["payables", "receivables"];

  const documentTypeLabels = {
    expense: "Expense",
    invoice: "Invoice",
    bill: "Bill",
    payables: "Bills/Payables",
    receivables: "Invoices/Receivables",
  };

  return (
    <div style={{ display: "flex", gap: "1rem", height: "100%" }}>
      {/* Main Content Area - 70% width */}
      <div style={{ flex: "0 0 70%", overflow: "auto" }}>
        <Formik
          initialValues={{
            docType: invoice?.docType || documentTypeOptions[0],
          }}
          enableReinitialize={true}
          onSubmit={(values) => {
            //
          }}
        >
          {({ values, setFieldValue }) => (
            <>
              <Row>
                <Col>
                  <FormGroup className="invoice-tour-export-to">
                    <strong>
                      <Form.Label>Export To</Form.Label>
                    </strong>
                    <ExportTypeInvoiceDropdown />
                  </FormGroup>
                </Col>
                <Col>
                  <Form.Group
                    controlId="select-document-type"
                    className="invoice-tour-document-type"
                  >
                    <Form.Label>
                      <strong>Document Type</strong>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={values.docType}
                      onChange={(e) => {
                        setFieldValue("docType", e.target.value);
                        handleFieldChange("docType", e.target.value);
                      }}
                    >
                      {documentTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {documentTypeLabels[type]}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Formik>

        <div>
          <div className="invoice-tour-fields">
            {exportTo === "qbo" ? (
              selectedTransactionLine && (
                <InvoiceQbo
                  key={`${documentId}-${transactionLineRowNumber}`}
                  invoice={invoice}
                  getMarginTop={getMarginTop}
                  tl={selectedTransactionLine}
                />
              )
            ) : (
              <InvoiceExcel
                key={`${documentId}-${transactionLineRowNumber}`}
                invoice={invoice}
                transactionLines={transactionLines}
                getMarginTop={getMarginTop}
              />
            )}
          </div>
        </div>

        {exportTo !== "qbo" && <DownloadInvoiceModal />}
      </div>

      {/* Right Sidebar - 30% width */}
      <div
        style={{
          flex: "0 0 30%",
          backgroundColor: "#f8f9fa",
          padding: "1rem",
          borderLeft: "1px solid #dee2e6",
          overflow: "auto",
        }}
      >
        {/* <h5 className="mb-4 text-center">
          <FontAwesomeIcon icon="cog" className="mr-2" />
          Invoice Management
        </h5> */}

        {/* 1. Invoice Summary */}
        {/* <div
          className="mb-4 p-3"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h6 className="mb-3 text-primary">
            <FontAwesomeIcon icon="file-invoice-dollar" className="mr-2" />
            Invoice Summary
          </h6>
          <div className="mb-2">
            <small className="text-muted">Total Amount:</small>
            <br />
            <strong className="text-success">
              ${parseFloat(String(invoice?.total || 0)).toFixed(2)}
            </strong>
          </div>
          <div className="mb-2">
            <small className="text-muted">Document Type:</small>
            <br />
            <Badge variant="secondary">{invoice?.docType || "N/A"}</Badge>
          </div>
          <div className="mb-2">
            <small className="text-muted">Vendor:</small>
            <br />
            <span>{invoice?.vendor || "Not specified"}</span>
          </div>
          <div className="mb-2">
            <small className="text-muted">Date:</small>
            <br />
            <span>{invoice?.date || "Not specified"}</span>
          </div>
          <div className="mb-2">
            <small className="text-muted">Due Date:</small>
            <br />
            <span>{invoice?.dueDate || "Not specified"}</span>
          </div>
          <div className="mb-2">
            <small className="text-muted">Status:</small>
            <br />
            <Badge variant="info">{invoice?.status || "Draft"}</Badge>
          </div>
        </div> */}

        {/* 2. Approval Section */}
        <div
          className="mb-4 p-3"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h6 className="mb-3 text-primary">
            <FontAwesomeIcon icon="check-circle" className="mr-2" />
            Approvals
          </h6>

          {!approvalStatus.loading && approvalStatus.status && (
            <div className="mb-3">
              <small className="text-muted">Current Status:</small>
              <br />
              <Badge
                variant={
                  approvalStatus.status.status === "approved"
                    ? "success"
                    : approvalStatus.status.status === "rejected"
                    ? "danger"
                    : "warning"
                }
                className="mb-2"
              >
                {approvalStatus.status.status}
              </Badge>
            </div>
          )}

          {/* Approval Progress */}
          {approvalStatus.approvalProgress && (
            <div className="mb-3">
              <small className="text-muted">
                Progress: {approvalStatus.approvalProgress.approved}/
                {approvalStatus.approvalProgress.total} approved
              </small>
              <div className="progress mt-1" style={{ height: "6px" }}>
                <div
                  className="progress-bar bg-success"
                  style={{
                    width: `${
                      (approvalStatus.approvalProgress.approved /
                        approvalStatus.approvalProgress.total) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Approval Actions */}
          {!approvalStatus.loading &&
            (approvalStatus.needsApproval || approvalStatus.status) && (
              <div>
                {approvalStatus.isApprover &&
                  approvalStatus.status?.status === "pending" &&
                  !approvalStatus.approvalProgress?.requirements?.find(
                    (req) =>
                      req.isCurrentUser &&
                      ["approved", "rejected"].includes(req.status)
                  ) && (
                    <div className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Optional comment"
                        value={approvalComment}
                        onChange={(e) => setApprovalComment(e.target.value)}
                        className="mb-2"
                        size="sm"
                      />
                      <div className="d-grid gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={handleApprove}
                          disabled={approvalStatus.approvalActionLoading}
                          className="mr-1"
                        >
                          <FontAwesomeIcon icon="check" className="mr-1" />
                          {approvalStatus.approvalActionLoading
                            ? "Approving..."
                            : "Approve"}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleReject}
                          disabled={approvalStatus.approvalActionLoading}
                          className="mr-1"
                        >
                          <FontAwesomeIcon icon="times" className="mr-1" />
                          {approvalStatus.approvalActionLoading
                            ? "Rejecting..."
                            : "Reject"}
                        </Button>
                      </div>
                    </div>
                  )}

                {/* Submit for Approval */}
                {approvalStatus.needsApproval && !approvalStatus.status && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmitForApproval}
                    className="w-100 mb-2"
                    disabled={approvalStatus.approvalActionLoading}
                  >
                    <FontAwesomeIcon icon="paper-plane" className="mr-1" />
                    Submit for Approval
                  </Button>
                )}

                {/* Resubmit for Approval */}
                {approvalStatus.status?.status === "rejected" && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmitForApproval}
                    className="w-100 mb-2"
                    disabled={approvalStatus.approvalActionLoading}
                  >
                    <FontAwesomeIcon icon="redo" className="mr-1" />
                    Resubmit for Approval
                  </Button>
                )}

                <div className="d-grid gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowHistory(true)}
                  >
                    <FontAwesomeIcon icon="history" className="mr-1" />
                    View Approval History
                  </Button>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => setShowApprovalRules(!showApprovalRules)}
                  >
                    <FontAwesomeIcon icon="edit" className="mr-1" />
                    Edit Approval Rules
                  </Button>
                </div>
              </div>
            )}

          {/* Show message when no approval needed */}
          {!approvalStatus.loading &&
            !approvalStatus.needsApproval &&
            !approvalStatus.status && (
              <div className="text-center text-muted">
                <FontAwesomeIcon
                  icon="check-circle"
                  className="mb-2"
                  size="2x"
                />
                <br />
                <small>No approval required</small>
                <div className="mt-3">
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => setShowApprovalRules(!showApprovalRules)}
                  >
                    <FontAwesomeIcon icon="edit" className="mr-1" />
                    Edit Approval Rules
                  </Button>
                </div>
              </div>
            )}
        </div>

        {/* 3. Automation Rules */}
        <div
          className="mb-4 p-3"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h6 className="mb-3 text-primary">
            <FontAwesomeIcon icon="robot" className="mr-2" />
            Automation Rules
            <a
              href="https://www.docuclipper.com/docs/setting-up-invoice-rules/"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2"
              style={{ fontSize: "12px", opacity: 0.7 }}
            >
              <FontAwesomeIcon icon="question-circle" />
            </a>
          </h6>
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => dispatch(setShowModalInvoiceRules(true))}
            className="w-100"
          >
            <FontAwesomeIcon icon="cog" className="mr-1" />
            Edit Invoice Rules
          </Button>
        </div>

        {/* 4. Bank Transaction Matching */}
        <div
          className="mb-4 p-3"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h6 className="mb-3 text-primary">
            <FontAwesomeIcon icon="link" className="mr-2" />
            Bank Transaction Matching
            <a
              href="https://www.docuclipper.com/docs/invoice-to-bank-transaction-matching-guide/"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2"
              style={{ fontSize: "12px", opacity: 0.7 }}
            >
              <FontAwesomeIcon icon="question-circle" />
            </a>
          </h6>
          {selectedTransactionLine?.id && (
            <div className="mb-3">
              <InvoiceMatchStatus
                invoiceTransactionLineId={Number(selectedTransactionLine.id)}
                refreshTrigger={matchStatusKey}
              />
            </div>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowMatcher(!showMatcher)}
            className="w-100"
          >
            <FontAwesomeIcon icon="link" className="mr-1" />
            {showMatcher ? "Hide" : "Match to"} Bank Transactions
          </Button>
        </div>

        {/* 5. Purchase Order Matching */}
        <div
          className="mb-4 p-3"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h6 className="mb-3 text-primary">
            <FontAwesomeIcon icon="file-invoice" className="mr-2" />
            Purchase Order Matching
            <a
              href="https://www.docuclipper.com/docs/invoice-to-purchase-order-matching-guide/"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2"
              style={{ fontSize: "12px", opacity: 0.7 }}
            >
              <FontAwesomeIcon icon="question-circle" />
            </a>
          </h6>
          {selectedTransactionLine?.id && (
            <div className="mb-3">
              <InvoicePOMatchStatus
                invoiceTransactionLineId={Number(selectedTransactionLine.id)}
                refreshTrigger={poMatchStatusKey}
              />
            </div>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowPOMatcher(!showPOMatcher)}
            className="w-100"
          >
            <FontAwesomeIcon icon="file-invoice" className="mr-1" />
            {showPOMatcher ? "Hide" : "Match to"} Purchase Orders
          </Button>
        </div>
      </div>

      {/* Drawers and Modals */}
      <InvoiceTransactionMatcher
        isOpen={showMatcher}
        onToggle={() => setShowMatcher(!showMatcher)}
        invoiceTransactionLine={selectedTransactionLine}
        invoice={invoice}
        onMatchesUpdated={handleMatchesUpdated}
      />

      <InvoicePOMatchManager
        isOpen={showPOMatcher}
        onToggle={() => setShowPOMatcher(!showPOMatcher)}
        invoiceTransactionLine={selectedTransactionLine}
        invoice={invoice}
        onMatchesUpdated={handlePOMatchesUpdated}
      />

      <Modal
        show={showApprovalRules}
        onHide={() => setShowApprovalRules(false)}
        size="xl"
      >
        <Modal.Header
          closeButton
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
          placeholder=""
        >
          <Modal.Title>Invoice Approval Rules</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InvoiceApprovalRules />
        </Modal.Body>
      </Modal>

      <ApprovalHistory
        show={showHistory}
        onHide={() => setShowHistory(false)}
        transactionLineId={selectedTransactionLine?.id}
      />
    </div>
  );
};

export default InvoiceContainer;
